import { useState, useEffect, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { LuX, LuSearch } from "react-icons/lu";
import toast from "react-hot-toast";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
} from "@floating-ui/react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";

import API from "../api";
import { capture } from "../instrument";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;
const STALE_TIME_MS = 60_000;
const POPOVER_Z_INDEX = 60;

const fetchCorrespondantSuggestions = async (query, { signal }) => {
  if (!query || query.length < MIN_QUERY_LENGTH) return [];
  const response = await API.get(`/correspondant?q=${encodeURIComponent(query)}`, { signal });
  if (!response.ok) {
    toast.error(response.message || "Erreur lors de la recherche");
    return [];
  }
  return response.data || [];
};

const CorrespondantLookup = ({
  initialCorrespondant = undefined,
  onCorrespondantSelect,
  onCorrespondantRemove,
  placeholder = "Correspondant",
  searchBarOnly = false,
  autocompleteEnabled = false,
  disabled = false,
  removable = false,
  icon = null,
}) => {
  const [selectedCorrespondant, setSelectedCorrespondant] = useState({});
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [userDismissed, setUserDismissed] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);

  const inputRef = useRef(null);
  const selectedItemRef = useRef(null);

  useEffect(() => {
    if (!initialCorrespondant?.code && !initialCorrespondant?.name) return;
    setSelectedCorrespondant(initialCorrespondant);
  }, [initialCorrespondant]);

  useEffect(() => {
    if (!isEditing) return;
    inputRef.current?.focus();
    inputRef.current?.select?.();
  }, [isEditing]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedInputValue(inputValue), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const normalizedQuery = debouncedInputValue.trim();
  const queryEnabled = autocompleteEnabled === true && normalizedQuery.length >= MIN_QUERY_LENGTH;

  const { data, isFetching, error } = useQuery({
    queryKey: ["correspondant", normalizedQuery],
    queryFn: ({ signal }) => fetchCorrespondantSuggestions(normalizedQuery, { signal }),
    enabled: queryEnabled,
    retry: false,
    staleTime: STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });

  const suggestions = queryEnabled ? data || [] : [];
  const isLoading = queryEnabled && isFetching;

  useEffect(() => {
    if (!error) return;
    if (error?.name === "AbortError") return;
    capture(error);
    toast.error("Une erreur est survenue lors de la recherche du correspondant");
  }, [error]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [normalizedQuery]);

  useEffect(() => {
    if (selectedItemRef.current) selectedItemRef.current.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const hasQuery = inputValue.trim().length >= MIN_QUERY_LENGTH;
  const showPopover = disabled !== true && isFocused && hasQuery && !userDismissed;

  const { refs, floatingStyles, context } = useFloating({
    open: showPopover,
    onOpenChange: (next) => {
      if (next === false) setUserDismissed(true);
    },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(0),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${availableHeight}px`,
          });
        },
      }),
    ],
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);

  const trimmedInput = inputValue.trim();
  const footerVisible = trimmedInput.length >= MIN_QUERY_LENGTH;
  const totalItems = suggestions.length + (footerVisible ? 1 : 0);
  const footerSelected = footerVisible && selectedIndex === suggestions.length;

  const selectCorrespondant = (correspondant) => {
    if (disabled === true) return;
    if (searchBarOnly === false) setSelectedCorrespondant(correspondant);
    onCorrespondantSelect(correspondant);
    setInputValue("");
    setUserDismissed(false);
    if (searchBarOnly === true) {
      inputRef.current?.focus();
      return;
    }
    setIsEditing(false);
    inputRef.current?.blur();
  };

  const addManualCorrespondant = () => {
    selectCorrespondant({ code: trimmedInput, name: "", manual: true });
  };

  const startEditing = () => {
    if (disabled === true) return;
    setInputValue(selectedCorrespondant?.code || "");
    setUserDismissed(false);
    setIsEditing(true);
  };

  const stopEditing = () => {
    setIsEditing(false);
    setInputValue("");
  };

  const removeSelectedCorrespondant = () => {
    if (disabled === true) return;
    setSelectedCorrespondant({});
    onCorrespondantRemove?.();
  };

  const navOpts = { enabled: showPopover && totalItems > 0, enableOnFormTags: ["INPUT"], preventDefault: true };
  useHotkeys("down", () => { setIsKeyboardNavigating(true); setSelectedIndex((i) => Math.min(totalItems - 1, i + 1)); }, navOpts, [showPopover, totalItems]);
  useHotkeys("up", () => { setIsKeyboardNavigating(true); setSelectedIndex((i) => Math.max(0, i - 1)); }, navOpts, [showPopover, totalItems]);
  useHotkeys(
    "enter",
    () => {
      if (footerSelected === true) addManualCorrespondant();
      else if (suggestions[selectedIndex]) selectCorrespondant(suggestions[selectedIndex]);
    },
    navOpts,
    [showPopover, totalItems, footerSelected, suggestions, selectedIndex],
  );

  const onKeyDown = (event) => {
    if (event.key === "Escape" && isEditing === true) {
      event.preventDefault();
      stopEditing();
      inputRef.current?.blur();
    }
  };

  const onPanelMouseMove = () => {
    if (isKeyboardNavigating === true) setIsKeyboardNavigating(false);
  };

  if (autocompleteEnabled !== true) {
    const updateField = (field) => (event) => {
      if (disabled === true) return;
      const nextCorrespondant = { ...selectedCorrespondant, [field]: event.target.value };
      setSelectedCorrespondant(nextCorrespondant);
      onCorrespondantSelect(nextCorrespondant);
    };

    return (
      <div className="flex gap-1">
        <input
          type="text"
          value={selectedCorrespondant.code || ""}
          onChange={updateField("code")}
          disabled={disabled}
          placeholder="Code"
          className="field-input min-w-0 flex-1 px-2 py-1"
        />
        <input
          type="text"
          value={selectedCorrespondant.name || ""}
          onChange={updateField("name")}
          disabled={disabled}
          placeholder="Nom"
          className="field-input min-w-0 flex-1 px-2 py-1"
        />
      </div>
    );
  }

  const hasValue = searchBarOnly === false && Boolean(selectedCorrespondant.code || selectedCorrespondant.name);
  const isManual = selectedCorrespondant.manual === true;
  const displayValue = hasValue
    ? [[selectedCorrespondant.code, selectedCorrespondant.name].filter(Boolean).join(" — "), isManual && "(manuel)"].filter(Boolean).join(" ")
    : "";

  const showChip = removable === true && hasValue === true && isEditing === false;

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      {showChip ? (
        <div
          className={`group flex h-[32px] w-full items-center gap-2 rounded-md border border-transparent px-2 text-left transition-colors ${
            disabled === true ? "" : "hover:border-gray-200 hover:bg-gray-50"
          }`}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="min-w-0 flex-1 truncate text-[13px] text-gray-800">
              <span className="font-semibold">{selectedCorrespondant.code || selectedCorrespondant.name}</span>
              {selectedCorrespondant.code && selectedCorrespondant.name && <span className="text-gray-700"> — {selectedCorrespondant.name}</span>}
              {isManual && <span className="ml-1 text-primary-600">(manuel)</span>}
            </span>
            {icon && <span className="shrink-0">{icon}</span>}
          </div>
          {disabled !== true && (
            <button
              type="button"
              title="Supprimer le correspondant"
              onClick={(event) => {
                event.stopPropagation();
                removeSelectedCorrespondant();
              }}
              className="shrink-0 rounded p-0.5 text-gray-400 opacity-0 transition-opacity hover:bg-gray-200 hover:text-gray-600 group-hover:opacity-100 focus:opacity-100"
            >
              <LuX className="size-3.5" />
            </button>
          )}
        </div>
      ) : (
        <div
          ref={refs.setReference}
          {...getReferenceProps()}
          className={
            disabled === true
              ? "flex h-[32px] w-full cursor-not-allowed items-center gap-2 rounded-md border border-transparent bg-gray-200 px-2 opacity-50 transition-colors"
              : `field-shell${showPopover ? " rounded-b-none" : ""}`
          }
        >
          <input
            ref={inputRef}
            type="text"
            value={isEditing ? inputValue : displayValue}
            onChange={(event) => {
              setInputValue(event.target.value);
              setUserDismissed(false);
              if (Boolean(selectedCorrespondant?.code) === true) {
                setSelectedCorrespondant({});
                onCorrespondantRemove?.();
              }
            }}
            onKeyDown={onKeyDown}
            onFocus={() => {
              setIsFocused(true);
              setUserDismissed(false);
              if (isEditing === false) startEditing();
            }}
            onBlur={() =>
              setTimeout(() => {
                setIsFocused(false);
                if (isEditing === true) stopEditing();
              }, 150)
            }
            disabled={disabled}
            placeholder={placeholder}
            aria-label="Rechercher un correspondant"
            className="min-w-0 flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-400 disabled:cursor-not-allowed"
          />
          {isFocused ? (
            isLoading ? (
              <div className="field-spinner" />
            ) : (
              <LuSearch className="size-4 shrink-0 text-gray-400" />
            )
          ) : (
            icon && <span className="shrink-0">{icon}</span>
          )}
        </div>
      )}

      {showPopover && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: POPOVER_Z_INDEX }}
            className="flex flex-col"
            {...getFloatingProps()}
          >
            <div
              className="suggestion-panel"
              data-modal="true"
              onMouseMove={onPanelMouseMove}
            >
              {suggestions.length > 0 && (
                <ul role="list" className="min-h-0 flex-1 overflow-auto py-1">
                  {suggestions.map((correspondant, index) => {
                    const isSelected = index === selectedIndex;
                    const key = correspondant._id || `${correspondant.code || ""}-${index}`;
                    return (
                      <li
                        key={key}
                        ref={isSelected ? selectedItemRef : null}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          selectCorrespondant(correspondant);
                        }}
                        onMouseEnter={() => {
                          if (isKeyboardNavigating === false) setSelectedIndex(index);
                        }}
                        className="suggestion-item"
                        data-selected={isSelected}
                      >
                        <div className="flex min-w-0 flex-col px-3 py-1.5">
                          <div className="flex items-baseline gap-2 truncate">
                            <span className={`text-sm font-semibold ${isSelected ? "text-primary-700" : "text-gray-900"}`}>{correspondant.code}</span>
                            {correspondant.name && <span className="truncate text-xs text-gray-600">{correspondant.name}</span>}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {hasQuery && isLoading && suggestions.length === 0 && (
                <div className="suggestion-empty">Recherche…</div>
              )}

              {hasQuery && !isLoading && suggestions.length === 0 && footerVisible === false && (
                <div className="suggestion-empty">Aucun correspondant trouvé</div>
              )}

              {footerVisible === true && (
                <button
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    addManualCorrespondant();
                  }}
                  onMouseEnter={() => {
                    if (isKeyboardNavigating === false) setSelectedIndex(suggestions.length);
                  }}
                  className="suggestion-footer"
                  data-selected={footerSelected}
                >
                  <span className="suggestion-footer-badge">+</span>
                  <span className="truncate">Ajouter « {trimmedInput} » manuellement</span>
                </button>
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default CorrespondantLookup;
